/** @prettier */
/* eslint-disable import/no-cycle */
import { all, takeEvery, fork, call, put, take, select } from 'redux-saga/effects';
import api from 'services';
import {
  transformAccounts,
  transformAccount,
  transformPlatformAccount,
  transformEditedAccount,
  prepEditMyAccount,
  prepAccount,
  filterAccounts,
  prepValidateConnection,
} from 'transform/account';
import _ from 'lodash';
import { loadState } from 'utils/localStorage';
import { prepUserFromAccount } from 'transform/user';
import platformApi from 'services/platform';
import { showToast } from 'utils/toast';
import appActions from '../app/actions';
import actions from './actions';
import { mergeDeleteRequests } from 'utils';

const entityType = 'accounts';

export function* filterAccountsSaga() {

  yield takeEvery(actions.FILTER_ACCOUNTS, function* ({ filters }) {
    const state = yield select();
    const source = _.get(state, 'Accounts.source', []);
    const filtersExisting = _.get(state, 'Accounts.filters', {});
    const cleaned = _.omitBy({ ...filtersExisting, ...filters }, _.isNil);
    yield put({ type: actions.FILTER_ACCOUNTS_SUCCESS, data: filterAccounts(source, cleaned), filters: cleaned });
  });
}

export function* selectAccountHookSaga() {
  yield takeEvery(actions.SELECT_ACCOUNT, function* ({ selectedItem }) {
    const selectedAccount = yield call(api.fetchItem, {
      entityType: 'accounts',
      transform: transformAccount,
      id: selectedItem?.id,
    });
    yield put({ type: actions.CLEAR_DEFAULT_LANGS_MODEL });
    yield put({
      type: actions.FETCH_ACCOUNT_ETL_CONFIG,
      selectedItem: selectedAccount,
    });
    yield put({
      type: actions.SELECT_ACCOUNT_SUCCESS,
      selectedItem: selectedAccount,
    });
  });
}

export function* fetchEtlConfigSaga() {
  yield takeEvery(actions.FETCH_ACCOUNT_ETL_CONFIG, function* ({ selectedItem, isMyAccount = false }) {
    if (selectedItem?.allowSuperAdminAccess || isMyAccount) {
      const etlConfig = yield call(api.fetch, {
        entityType: 'etl-destination/config',
        activeAccountIdOverride: selectedItem.id,
      });
      if (etlConfig[0]?.type === 'AZURE') {
        _.set(selectedItem, 'etlConfigAzure', etlConfig[0]);
      } else if (etlConfig[0]?.type === 'S3') {
        _.set(selectedItem, 'etlConfig', etlConfig[0]);
      } else {
        _.set(selectedItem, 'etlConfig', {});
        _.set(selectedItem, 'etlConfigAzure', {});
      }

      if (isMyAccount) {
        const activeAccountEtlConfig = {
          etlConfigAzure: {},
          etlConfig: {},
        };
        if (etlConfig[0]?.type === 'AZURE') {
          activeAccountEtlConfig.etlConfigAzure = etlConfig[0];
        } else if (etlConfig[0]?.type === 'S3') {
          activeAccountEtlConfig.etlConfig = etlConfig[0];
        }
        yield put({ type: appActions.FETCH_ACCOUNT_ETL_CONFIG_SUCCESS, etlConfig: activeAccountEtlConfig });
      } else {
        yield put({ type: actions.FETCH_ACCOUNT_ETL_CONFIG_SUCCESS, data: selectedItem });
      }
    }
  });
}

export function* fetchTTLSaga() {
  yield takeEvery(actions.FETCH_TTL, function* ({ accountID }) {
    const data = yield call(api.fetchItem, { entityType: 'ttl', id: accountID, activeAccountIdOverride: accountID });
    if (data) {
      yield put(actions.fetchTTLSuccess(data));
    } else {
      yield put(actions.fetchTTLFailure('Something went wrong'));
    }
  });
}

export function* updateDefaultSpeechModelSaga() {
  yield takeEvery(actions.UPDATE_DEFAULT_SPEECH_MODEL, function* ({ data = {}, accountID: id = '' }) {
    const res = yield call(api.editItem, {
      entityType: 'speechModel',
      data,
      id,
      activeAccountIdOverride: id,
    });
    if (res) {
      yield put(actions.updateDefaultSpeechModelSuccess(res));
    } else {
      yield put(actions.updateDefaultSpeechModelFailure('Something went wrong'));
    }
  });
}

export function* fetchAccountsSaga() {
  yield takeEvery(actions.FETCH_ACCOUNTS, function* ({ filters, clearExistingFilters = true, url }) {
    yield put({ type: actions.CHANGE_INIT_STATUS, init: false});
    const state = yield select();
    const filtersExisting = _.get(state, 'Accounts.filters', {});
    const filtersCleaned = clearExistingFilters ? filters : _.omitBy({ ...filtersExisting, ...filters }, _.isNil);
    const data = yield call(api.get, { url: url ? `accounts/paginated?${url}`  : `accounts/paginated`, disableNotification: true,});
    const accountsData = transformAccounts(data.data)
    yield put({ type: actions.CHANGE_INIT_STATUS, init: true});
    // yield put({ type: actions.FETCH_ACCOUNTS_SUCCESS, data: accountsData});
    yield put({
      type: actions.FILTER_ACCOUNTS_SUCCESS,
      data: filterAccounts(accountsData, filtersCleaned),
      filters: filtersCleaned,
      pagination: data?.data?.pagination
    });
  });
}

export function* fetchCategorizationJobs() {
  yield takeEvery(actions.FETCH_CATEGORIZATION_JOBS, function* ({}) {
    const data = yield call(api.fetchAll, { entityType: 'managementJobs' });
    if (data) {
      yield put({ type: actions.FETCH_CATEGORIZATION_JOBS_SUCCESS, data });
      showToast('success', { task: 'Successfully fetched categorization jobs' });
    }
  });
}

function* setCurrentAccount(accounts = [], currentAccount) {
  const state = yield select();

  const appActiveAccountId = _.get(state, 'App.activeAccountId');
  const localActiveAccountId = loadState('activeAccountId');
  const activeAccountId = localActiveAccountId || currentAccount?.id;

  let activeAccount = currentAccount;
  if (_.isEmpty(activeAccount)) {
    const accountOption = accounts.find((x) => x.id === activeAccountId);
    activeAccount = accountOption || accounts[0];
  }

  if (!appActiveAccountId && activeAccount) {
    yield put({
      type: appActions.CHANGE_ACTIVE_ACCOUNT,
      activeAccountId: activeAccount.id,
      activeAccount: transformAccount(activeAccount),
    });
  }
}

function* refreshSelectedAccountSaga() {
  yield takeEvery(actions.REFRESH_SELECTED_ACCOUNT, function* () {
    try {
      const state = yield select();
      const id = _.get(state, 'Accounts.selectedItem.id');
      if (id) {
        const account = yield call(api.fetchItem, { entityType: 'accounts', id });
        // Safely try to fetch bulk settings
        let bulkDownloadEnabled = false;
        try {
          const bulkSetting = yield call(platformApi.platformGetReq, {
            urlPath: `account/${id}/settings/bulk-download`,
            disableNotification: true,
            disableErrNotification: true
          });
          bulkDownloadEnabled = bulkSetting?.bulkDownloadEnabled || false;
        } catch (e) {
          // Log the error or handle it as needed
          console.warn('Failed to fetch bulk download setting:', e);
        }
        const data = [...state.Accounts.data.filter((i) => i.id !== account.id)];
        const selectedItem = transformAccount(account);
        selectedItem.bulkDownloadEnabled = bulkDownloadEnabled;
        data.push(selectedItem);
        yield put({ type: actions.REFRESH_ACCOUNT_SUCCESS, selectedItem, data, notSetListData: true });
        selectedItem.etlConfig = {};
        yield put({ type: actions.FETCH_ACCOUNT_ETL_CONFIG, selectedItem });
        // Handle case when user is activating the active account
        if (selectedItem.id === state.App.activeAccount.id) {
          const action = yield take(actions.FETCH_ACCOUNT_ETL_CONFIG_SUCCESS);
          yield put({ type: appActions.CHANGE_ACTIVE_ACCOUNT, activeAccount: action.data, clear: false });
        }
        yield put({ type: actions.SET_PRODUCTS_LOADING, loadingState: false });
      }
    } catch (err) {
      yield put({ type: actions.SET_PRODUCTS_LOADING, loadingState: false });

      console.error('Error in refreshSelectedAccountSaga:', err);
    }
  });
}

/**
 * Fetch Accounts for SuperAdmin dropdown
 */
export function* fetchAccountsOptionsSaga() {
  yield takeEvery(actions.FETCH_ACCOUNT_OPTIONS, function* ({ url }) {
    const state = yield select();
    const accountsStore = _.get(state, 'Accounts');
    const endpoint = url ? `accounts/paginated?${url}` : `accounts/paginated`;

    try {
      const paginatedAccountsRes = yield call(api.get, {
        url: endpoint,
        disableNotification: true,
      });

      let accounts = !accountsStore.init
        ? paginatedAccountsRes?.data?.accounts || []
        : accountsStore.data;

      yield put({ type: appActions.SET_ADMIN_INIT, adminInit: true });

      const localActiveAccountId = loadState('activeAccountId');
      const currentAccount = yield call(resolveCurrentAccount, accounts, localActiveAccountId, url);

      // Add currentAccount to list if fetched from API and not already in list
      if (currentAccount && !accounts.find((acc) => acc.id === currentAccount.id)) {
        accounts = [...accounts, currentAccount];
      }

      yield put({
        type: actions.FETCH_ACCOUNT_OPTIONS_SUCCESS,
        data: accounts,
        initialPagination : paginatedAccountsRes?.data?.pagination
      });
      yield put({ type: actions.SET_SOURCE_PAGINATION, pagination: paginatedAccountsRes?.data?.pagination });

      yield setCurrentAccount(accounts, currentAccount);
    } catch (e) {
      console.error('Failed to fetch account options:', e);
    }
  });
}

function* resolveCurrentAccount(accounts, activeAccountId, url) {
  if (activeAccountId) {
    let current = accounts?.find((acc) => acc.id === activeAccountId);
    if (!current) {
      const selected = yield call(api.fetchItem, {
        entityType: 'accounts',
        transform: transformAccount,
        id: activeAccountId,
      });
      return selected;
    }
    return current;
  }

  let voiceBaseAccount = accounts?.find((acc) => acc.name === 'VoiceBase');
  if (!voiceBaseAccount) {
    const voiceBaseSearchUrl = url
      ? `accounts/paginated?${url}&searchByNameOrId=voicebase`
      : `accounts/paginated?searchByNameOrId=voicebase`;

    const voiceBaseRes = yield call(api.get, {
      url: voiceBaseSearchUrl,
      disableNotification: true,
    });
    voiceBaseAccount = voiceBaseRes?.data?.accounts?.find((acc) => acc.name === 'VoiceBase');
  }

  return voiceBaseAccount;
}

export function* updateVbAccessSaga() {
  yield takeEvery(actions.UPDATE_VB_ACCESS, function* ({ access, id }) {
    const state = yield select();
    const data = { allowSuperAdminAccess: access };
    const { activeAccount } = state.App;
    const activeAccountIdOverride = id || activeAccount.id;
    yield call(api.patchItem, { entityType: 'account', data, activeAccountIdOverride });
    yield put({
      type: appActions.CHANGE_ACTIVE_ACCOUNT,
      activeAccount: { ...activeAccount, ...data },
    });
  });
}

/**
 * Set accounts on start up
 */
export function* setAccessAccount() {
  yield takeEvery(actions.SET_ACCESS_ACCOUNTS, function* ({ accounts = [] }) {
    const localActiveAccountId = loadState?.('activeAccountId');
    const account = accounts.find((i) => i.id === localActiveAccountId) || accounts[0];
    if (account) {
      // EA-1281 only AccountAdmins can configure their etl destinations
      // const hasEAProduct = account.products.find((i) => i.name === 'EA');
      // const hasRoleAccess = ['AccountAdmin'].some((i) => account.roles.includes(i));
      // if (hasEAProduct && hasRoleAccess) {
      //   const etlConfig = yield call(api.fetch, { entityType: 'etl-destination/config', activeAccountIdOverride: account.id });
      //   if (etlConfig[0]?.type === 'AZURE') {
      //     account.etlConfigAzure = etlConfig[0];
      //   } else if (etlConfig[0]?.type === 'S3') {
      //     account.etlConfig = etlConfig[0];
      //   } else {
      //     account.etlConfig = {};
      //     account.etlConfigAzure = {};
      //   }
      // }
      yield put({ type: actions.SET_ACCESS_ACCOUNTS_SUCCESS, accounts });
      yield put({ type: appActions.SET_API_CONNECTED, apiConnected: true });
      yield setCurrentAccount(accounts, account);
    }
  });
}

export function* addAccountSaga() {
  yield takeEvery(actions.ADD_ACCOUNT, function* ({ data, onFinish,fetchUrl, isImportAccountFlow }) {
    try{
      yield put({ type: actions.TOGGLE_ACCOUNT_IS_LOADING, data: true });
      const userData = prepUserFromAccount({
        ...data,
      });
      const accountData = prepAccount(data);
      let account;
      if (isImportAccountFlow) {
       let importApi = yield call(api.post, {
          url: `accounts/import`,
          data: {
            account: accountData,
            adminUser: userData,
          },
          msg: 'Account imported successfully'
        });
        account = importApi.data;
      } else {
        account = yield call(api.createItem, {
          entityType,
          data: {
            account: accountData,
            adminUser: userData,
          },
        });
      };
      if (account && account?.id ) {
        if(isImportAccountFlow){
          yield call(api.toggleAccountProduct, {
            productType: 'EA_HOSTED',
            accountId: account.id,
            isActivating: true,
            isImportAccount: isImportAccountFlow
          });
          yield call(api.toggleAccountProduct, {
            productType: 'VBO',
            accountId: account.id,
            isActivating: true,
            isImportAccount: isImportAccountFlow
          });
        };
        yield put({ type: actions.ADD_ACCOUNT_SUCCESS, selectedItem: transformAccount(account) });
        yield put({ type: actions.CHANGE_ACCOUNT_FORM_VIEW, data: false });
        yield put({ type: actions.TOGGLE_ACCOUNT_IS_LOADING, data: false });
        yield put({ type: actions.FETCH_ACCOUNTS, url: fetchUrl });
      }else{
        yield put({ type: actions.TOGGLE_ACCOUNT_IS_LOADING, data: false });
      }
      onFinish(!!account);
    }catch(err){
      console.log(err)
    }

  });
}

export function* updateTTLSaga() {
  yield takeEvery(actions.UPDATE_TTL, function* ({ data: { ttl = {}, accountID: id = '' } }) {
    const rPayload = Object.fromEntries(
      Object.entries(ttl)
        .map(([k, v]) => [k, +v.slice(0, v.indexOf(' '))])
        .map(([k, v]) => [k, +v < 0 ? +v : +v * 1440])
    );
    const res = yield call(api.editItem, {
      entityType: 'ttl',
      data: rPayload,
      id,
      activeAccountIdOverride: id,
    });
    if (res) {
      yield put(actions.updateTTLSuccess(res));
    } else {
      yield put(actions.updateTTLFailure('Something went wrong'));
    }
  });
}

export function* editAccountSaga() {
  yield takeEvery(actions.EDIT_ACCOUNT, function* ({ data, id, onFinish, isMyAccount = false,fetchUrl }) {
    const account = yield call(api.editItem, {
      entityType,
      data: prepEditMyAccount(data),
      id,
      activeAccountIdOverride: id,
      needResponse: true,
    });
    yield put({ type: actions.FETCH_DEFAULT_LANGUAGE_MODEL, status: false });
    if (account.status === 200) {
      const updatedAccount = transformEditedAccount(account.data);
      yield put({ type: actions.FETCH_ACCOUNTS , url: fetchUrl});
      yield put({ type: actions.EDIT_ACCOUNT_SUCCESS, selectedItem: updatedAccount });
      if (isMyAccount) {
        yield put({ type: appActions.UPDATE_ACTIVE_ACCOUNT_LANGUAGE, language: updatedAccount.language });
        yield put({ type: appActions.CHANGE_ACTIVE_ACCOUNT_PCI, value: data.pciRedaction });
      }
      yield put({ type: actions.FETCH_DEFAULT_LANGUAGE_MODEL, status: false });
      if (account.data.account && account.data.account.language === null) {
        yield put({ type: actions.CLEAR_DEFAULT_LANGS_MODEL });
        yield put({ type: actions.FETCH_DEFAULT_LANGUAGE_MODEL, status: true });
      }
    } else {
      yield put({ type: actions.EDIT_ACCOUNT_FAILURE });
    }
    onFinish(!!account);
  });
}

export function* editMyAccountSaga() {
  yield takeEvery(actions.EDIT_MY_ACCOUNT, function* ({ data, onFinish = () => {} }) {
    const state = yield select();
    const accountPayload = prepEditMyAccount(data);
    const resData = yield call(api.editItem, { entityType: 'account', data: accountPayload });
    yield put({ type: actions.FETCH_DEFAULT_LANGUAGE_MODEL, status: false });
    if (resData?.account) {
      let transformedAccount = transformAccount(resData);
      transformedAccount = transformEditedAccount(transformedAccount);
      const newActiveAccount = { ...state.App.activeAccount, etlConfig: transformedAccount?.etlConfig };

      if (transformedAccount) {
        // somehow the account being sent from the server have roles array
        // empty so temporarily using the roles from prev obj
        transformedAccount.roles = state.App.activeAccount.roles;

        yield put({ type: actions.EDIT_MY_ACCOUNT_SUCCESS });
        yield put({ type: actions.EDIT_ACCOUNT_SUCCESS, selectedItem: transformedAccount });
        yield put({
          type: appActions.CHANGE_ACTIVE_ACCOUNT_SUCCESS,
          activeAccount: {
            ...newActiveAccount,
            ...transformedAccount,
            billingSameAs: transformedAccount.billingSameAs,
          },
          activeAccountId: transformedAccount.id,
        });
      } else {
        yield put({ type: actions.EDIT_MY_ACCOUNT_FAILURE });
      }
    } else {
      yield put({ type: actions.EDIT_MY_ACCOUNT_FAILURE });
    }

    onFinish(!!resData?.account);
  });
}

export function* fetchAccountSaga() {
  yield takeEvery(actions.FETCH_ACCOUNT, function* ({ id }) {
    if (id) {
      const account = yield call(api.fetchItem, { entityType, id });
      if (account) {
        const selectedItem = transformAccount(account);
        yield put({ type: actions.FETCH_ACCOUNT_SUCCESS, selectedItem });
      } else {
        yield put({ type: actions.FETCH_ACCOUNT_FAILURE });
      }
    }
  });
}

export function* fetchPlatformAccountSaga() {
  yield takeEvery(actions.FETCH_PLATFORM_ACCOUNT, function* ({ id }) {
    if (id) {
      const account = yield call(api.fetchPlatformItem, { entityType: 'account', id });
      if (account) {
        const selectedItemFromPlatform = transformPlatformAccount(account);
        yield put({ type: actions.FETCH_PLATFORM_ACCOUNT_SUCCESS, selectedItemFromPlatform });
      } else {
        yield put({ type: actions.FETCH_PLATFORM_ACCOUNT_FAILURE });
      }
    }
  });
}

export function* updateDefaultSpeechLanguage() {
  yield takeEvery(actions.UPDATE_SPEECH_DEFAUT_LANGUAGE, function* ({ data, selectedItem, totalLength, currentIndex }) {
    const langUpdate = yield call(platformApi.platformPutReq, {
      urlPath: `account/${selectedItem.id}/settings/default-speech-model/${data.language}`,
      data,
      entityType: 'languageSettings',
    });
    if (langUpdate) {
      if (totalLength - 1 === currentIndex) {
        yield put({ type: actions.EDIT_ACCOUNT_SUCCESS, selectedItem });
        yield put({ type: actions.EDIT_MY_ACCOUNT_SUCCESS });
        yield put({ type: actions.FETCH_DEFAULT_LANGUAGE_MODEL, status: true });
      }
      yield put({ type: actions.UPDATE_SPEECH_DEFAUT_LANGUAGE_SUCCESS });
    } else {
      yield put({ type: actions.UPDATE_SPEECH_DEFAUT_LANGUAGE_FAILURE });
    }
  });
}

export function* getDefaultSpeechLanguageSaga() {
  yield takeEvery(actions.GET_SPEECH_DEFAULT_LANGUAGE, function* ({ lng, accountID }) {
    const langUpdate = yield call(platformApi.platformGetReq, {
      urlPath: `account/${accountID}/settings/default-speech-model/${lng}`,
      disableNotification: true,
      activeAccountIdOverride: accountID,
    });
    if (langUpdate) {
      if (langUpdate.model === 'Proteus') {
        langUpdate.model = 'Europa';
      }
      yield put({
        type: actions.GET_SPEECH_DEFAULT_LANGUAGE_SUCCESS,
        data: { language: lng, model: langUpdate.model },
      });
    } else {
      yield put({ type: actions.GET_SPEECH_DEFAULT_LANGUAGE_FAILURE });
    }
  });
}

export function* getPredictiveModelSaga() {
  yield takeEvery(actions.GET_PREDICTIVE_MODEL, function* ({ accountID }) {
    const predictiveModels = yield call(platformApi.platformGetReq, {
      urlPath: 'definition/prediction/classifiers?ignoreDisabled=true',
      activeAccountIdOverride: accountID,
      disableNotification: true,
    });
    if (predictiveModels) {
      yield put({ type: actions.GET_PREDICTIVE_MODEL_SUCCESS, data: predictiveModels.classifiers });
    } else {
      yield put({ type: actions.GET_PREDICTIVE_MODEL_FAILURE });
    }
  });
}
export function* fetchMissingRecordsSaga() {
  yield takeEvery(actions.FETCH_MISSING_RECORDS, function* ({ dateRange, accountId }) {
    const { fromDate, toDate } = dateRange;
    const missingRecords = yield call(api.get, {
      url: `missingRecords?from=${fromDate}&to=${toDate}`,
      activeAccountIdOverride: accountId,
      disableNotification: true,
    });
    if (missingRecords && missingRecords?.status === 200) {
      yield put({ type: actions.FETCH_MISSING_RECORDS_SUCCESS, data: missingRecords.data });
    } else {
      yield put({ type: actions.FETCH_MISSING_RECORDS_FAILURE });
    }
  });
}
export function* fetchSkippedRecordsSaga() {
  yield takeEvery(actions.FETCH_SKIPPED_RECORDS, function* ({ dateRange, accountId }) {
    const { fromDate, toDate } = dateRange;
    const skippedRecords = yield call(api.get, {
      url: `skipRecords?from=${fromDate}&to=${toDate}`,
      activeAccountIdOverride: accountId,
      disableNotification: true,
    });
    if (skippedRecords && skippedRecords?.status === 200) {
      yield put({ type: actions.FETCH_SKIPPED_RECORDS_SUCCESS, data: skippedRecords.data });
    } else {
      yield put({ type: actions.FETCH_SKIPPED_RECORDS_FAILURE });
    }
  });
}

export function* validateConnection() {
  yield takeEvery(actions.VALIDATE_CONNECTION, function* ({ data, onFinish }) {
    const dataStorageType = data.dataStorage;
    try {
      const response = yield call(api.post, {
        url: `validate/connection/${dataStorageType.toLowerCase()}`,
        data: prepValidateConnection(data),
        msg: 'Connection Validated successfully',
      });
      if (response.status === 200) {
        yield put({ type: actions.VALIDATE_CONNECTION_SUCCESS, data: { message: '' } });
      }
      onFinish();
    } catch (err) {
      if (err) {
        yield put({ type: actions.VALIDATE_CONNECTION_FAILURE, data: err });
      }
      onFinish();
    }
  });
}

export function* getEmbededDashboardUrlSaga() {
  yield takeEvery(actions.FETCH_EMBEDED_DASHBOARD_URL, function* ({accountId}) {
    try {
      const response = yield call(api.get, {
        url: `tableau-sites`,
        activeAccountIdOverride: accountId,
        disableNotification: true
      });

      if (response.status === 200) {
        yield put({ type: actions.FETCH_EMBEDED_DASHBOARD_URL_SUCCESS, data: response.data });
      }
    } catch (err) {
      if (err) {
        // yield put({ type: actions.VALIDATE_CONNECTION_FAILURE, data: err });
      }
    }
  });
}

export function* updateEmbededDashboardUrlSaga() {
  yield takeEvery(actions.UPDATE_EMBEDED_DASHBOARD_URL, function* ({accountId, data, recordId}) {
    try {
      const response = yield call(api.put, {
        url: `tableau-sites/${recordId}`,
        activeAccountIdOverride: accountId,
        data,
        msg: 'Tableau embedded URL updated successfully',
      });
      if (response.status === 200) {
        yield put({ type: actions.FETCH_EMBEDED_DASHBOARD_URL, accountId });
      }
    } catch (err) {
      if (err) {
        // yield put({ type: actions.VALIDATE_CONNECTION_FAILURE, data: err });
      }
    }
  });
}

export function* initiateDeleteProcessSaga() {
  yield takeEvery(actions.INITIATE_DELETE_PROCESS, function* ({ accountId,email, cb }) {
    try {
      const response = yield call(api.post, {
        url: `accounts/${accountId}/initiate-cleanup`,
        data: {email},
        msg: 'Request has been initiated successfully',
      });
      if (response.status === 200) {
        cb(response)
      }else{
        cb(false)
      }
    } catch (err) {
      cb(false)
    }
  });
}

export function* fetchAllCleanupsSaga(){
  yield takeEvery(actions.FETCH_ALL_CLEANUPS, function* ({ accountId }) {
    try {
      const response = yield call(api.get, {
        url: `accounts/cleanup/status`,
        disableNotification: true,
      });
      const platformResponse = yield call(platformApi.platformGetReq, {
        urlPath: `delete-account-requests`,
        disableNotification: true
      });
      const mergedResponse = mergeDeleteRequests(response?.data,platformResponse?.deleteAccountRequests)
      if (response.status === 200) {
        yield put({ type: actions.FETCH_ALL_CLEANUPS_SUCCESS, data: mergedResponse });
      }
    } catch (err) {
      
    }
  });
  
}

export function* approveCleanupSaga(){
  yield takeEvery(actions.APPROVE_CLEANUP, function* ({ id, data, cb }) {
    try {
      const response = yield call(api.post, {
        url: `accounts/${id}/approve-cleanup`,
        data,
        msg: 'Request has been approved successfully',
      });
      if (response.status === 200) {
        yield put({ type: actions.FETCH_ALL_CLEANUPS });
        cb(response)
      }else{
        cb(false)
      }
    } catch (err) {
      cb(false)
    }
  });
}

export function* rejectCleanupSaga(){
  yield takeEvery(actions.REJECT_CLEANUP, function* ({ id, cb }) {
    try {
      const response = yield call(api.post, {
        url: `accounts/${id}/reject-cleanup`,
        data:{},
        msg: 'Request has been rejected successfully'
      });
      if (response.status === 200) {
        yield put({ type: actions.FETCH_ALL_CLEANUPS });
        cb(response)
      }else{
        cb(false)
      }
    } catch (err) {
      cb(false)
    }
  });
  
}

export function* getBulkDownloadConfigSetingSaga() {
  yield takeEvery(actions.FETCH_BULK_DOWNLOAD_CONFIG_SETING, function* ({ accountId, myAccount = false }) {
    try{
      const bulkSetting = yield call(platformApi.platformGetReq, {
        urlPath: `account/${accountId}/settings/bulk-download`,
        disableNotification: true,
        activeAccountIdOverride: accountId,
        disableErrNotification: true
      });
      yield put({ type: actions.SET_PRODUCTS_LOADING, loadingState: false });
      if(myAccount){
        yield put({ type: appActions.UPDATE_CONV_DOWNLOAD_CONFIG_STATUS,  bulkDownloadEnabled: bulkSetting?.bulkDownloadEnabled });
      }else{
        yield put({ type: actions.FETCH_BULK_DOWNLOAD_CONFIG_SETING_SUCCESS, bulkDownloadEnabled: bulkSetting.bulkDownloadEnabled });
      }
    }catch(err){
      console.log(err);
      yield put({ type: actions.FETCH_BULK_DOWNLOAD_CONFIG_SETING_SUCCESS, bulkDownloadEnabled: false });
      yield put({ type: actions.SET_PRODUCTS_LOADING, loadingState: false });

    }
    
  });
}

export function* updateBulkDownloadConfigSetingSaga() {
  yield takeEvery(actions.UPDATE_BULK_DOWNLOAD_CONFIG_SETING, function* ({ accountId,data,isActiveAccountSelected, cb }) {
    try{
      const bulkSetting = yield call(platformApi.platformPutReq, {
        urlPath: `account/${accountId}/settings/bulk-download`,
        disableNotification: true,
        data
      });
      yield put({ type: actions.SET_PRODUCTS_LOADING, loadingState: false });
      yield put({ type: actions.FETCH_BULK_DOWNLOAD_CONFIG_SETING, accountId });
      if (isActiveAccountSelected) {
        yield put({ type: appActions.UPDATE_CONV_DOWNLOAD_CONFIG_STATUS, bulkDownloadEnabled: bulkSetting.bulkDownloadEnabled });
      }
        cb(bulkSetting)
    }catch(err){
      console.log(err);
      yield put({ type: actions.UPDATE_CONV_DOWNLOAD_CONFIG_STATUS, bulkDownloadEnabled: false });
      yield put({ type: actions.SET_PRODUCTS_LOADING, loadingState: false });

    }
    
  });
}

export function* fetchAccountBySiteIdSaga() {
  yield takeEvery(actions.FETCH_ACCOUNTS_BY_SITEID, function* ({ siteId }) {
    const siteIdAccounts =yield call(api.get, { url: `accounts/by-siteId?siteId=${siteId}`, disableNotification: true,});
    if (siteIdAccounts?.data) {
      yield put({ type: actions.FETCH_ACCOUNTS_BY_SITEID_SUCCESS, data: siteIdAccounts?.data });
    }
  });
}

export function* setDefaultSiteIdAccountSaga() {
  yield takeEvery(actions.SET_DEFAULT_SITEID_ACCOUNT, function* ({ accountId, cb }) {
    let selectedAccount = yield call(api.fetchItem, {
      entityType: 'accounts',
      transform: transformAccount,
      id: accountId,
    });
    selectedAccount.preferredForTableau = true
    const apiResp = yield call(api.put, { url: `accounts/${accountId}/preferredForTableau`, disableNotification: false, data: prepEditMyAccount(selectedAccount), msg: 'Account updated successfully.'});
    if(apiResp.status === 200 ){
      cb(true)
    }else{
      cb(false)
    }
  });
}

export function* setSourceAccountsSaga() {
  yield takeEvery(actions.SET_SOURCE_ACCOUNTS_DATA, function* ({ url, cb, isSearch }) {
    const endpoint = url ? `accounts/paginated?${url}` : `accounts/paginated`;
    const paginatedAccountsRes = yield call(api.get, {
      url: endpoint,
      disableNotification: true,
    });
    let accounts = paginatedAccountsRes?.data?.accounts
    if(isSearch){
      const match = url.match(/searchByNameOrId=([^&]*)/);
      const searchValue = match ? decodeURIComponent(match[1]) : null;
      if(!searchValue ){
        const state = yield select();
        const appStore = _.get(state, 'App');
        const activeAccount = appStore.activeAccount;
        accounts = [...accounts, activeAccount]
      }
    }
    yield put({ type: actions.SET_SOURCE_ACCOUNTS_DATA_SUCCESS, accounts: accounts, isSearch });
    yield put({ type: actions.SET_SOURCE_PAGINATION, pagination: paginatedAccountsRes?.data?.pagination });
    cb?.()
  });
}

export default function* rootSaga() {
  yield all([
    fork(fetchAccountsSaga),
    fork(selectAccountHookSaga),
    fork(fetchEtlConfigSaga),
    fork(filterAccountsSaga),
    fork(fetchAccountsOptionsSaga),
    fork(updateVbAccessSaga),
    fork(addAccountSaga),
    fork(editAccountSaga),
    fork(editMyAccountSaga),
    fork(refreshSelectedAccountSaga),
    fork(fetchTTLSaga),
    fork(updateTTLSaga),
    fork(fetchPlatformAccountSaga),
    fork(setAccessAccount),
    fork(updateDefaultSpeechLanguage),
    fork(getDefaultSpeechLanguageSaga),
    fork(getPredictiveModelSaga),
    fork(validateConnection),
    fork(fetchMissingRecordsSaga),
    fork(fetchSkippedRecordsSaga),
    fork(fetchCategorizationJobs),
    fork(initiateDeleteProcessSaga),
    fork(fetchAllCleanupsSaga),
    fork(approveCleanupSaga),
    fork(rejectCleanupSaga),
    fork(getEmbededDashboardUrlSaga),
    fork(updateEmbededDashboardUrlSaga),
    fork(getBulkDownloadConfigSetingSaga),
    fork(updateBulkDownloadConfigSetingSaga),
    fork(fetchAccountBySiteIdSaga),
    fork(setDefaultSiteIdAccountSaga),
    fork(setSourceAccountsSaga)
  ]);
}
